import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {PaginationData} from '../DataView';
import Button from '../../../../../incase-domain/components/button/Button';

export enum PaginationPosition {
    TOP,
    BOTTOM,
}

interface PaginationProps {
    data: PaginationData;
    onPageChange: (value: number) => void;
}

export function Pagination(props: PaginationProps): JSX.Element | null {
    const {data, onPageChange} = props;

    const {t} = useTranslation();

    const numberOfPages = Math.ceil(data.itemsCount / data.pageSize);

    function isNumber(value: string): boolean {
        return !Number.isNaN(parseFloat(value)) && Number.isFinite(Number(value));
    }

    function getClosestValue(value: string): number {
        if (isNumber(value)) {
            const numValue = Number.parseInt(value);
            if (numValue < 1) return 1;
            if (numValue >= numberOfPages) return numberOfPages;
            return numValue;
        }
        return 1;
    }

    const [currentPage, setCurrentPage] = useState<string>(data.currentPage.toString());

    useEffect(() => {
        setCurrentPage(data.currentPage.toString());
    }, [data.currentPage]);

    function handleOnChange(e: React.ChangeEvent<HTMLInputElement>): void {
        const numValue = getClosestValue(e.target.value);

        if (numValue >= numberOfPages) {
            setCurrentPage(numberOfPages.toString());
        } else {
            setCurrentPage(e.target.value);
        }
        onPageChange(numValue);
    }

    function handleBlur(e: React.ChangeEvent<HTMLInputElement>): void {
        const numValue = getClosestValue(currentPage);
        onPageChange(numValue);
        setCurrentPage(getClosestValue(e.target.value).toString());
    }

    function handleOnPageBackClick(): void {
        const numValue = getClosestValue(currentPage);
        onPageChange(numValue - 1);
        setCurrentPage((numValue - 1).toString());
    }

    function handleOnPageNextClick(): void {
        const numValue = getClosestValue(currentPage);
        onPageChange(numValue + 1);
        setCurrentPage((numValue + 1).toString());
    }

    const pageBackEnabled = isNumber(currentPage) && parseInt(currentPage) > 1;
    const pageNextEnabled = isNumber(currentPage) && parseInt(currentPage) < numberOfPages;

    return (
        <div className="c-genericTable__paginator">
            <div className="c-genericTable__paginator_label">
                {`${(getClosestValue(currentPage) - 1) * data.pageSize + 1} - ${Math.min(
                    getClosestValue(currentPage) * data.pageSize,
                    data.itemsCount
                )} ${t('genericTable.of')} ${data.itemsCount}`}
            </div>
            <div className="u-flex u-gap-1">
                <Button
                    variant={!pageBackEnabled ? 'ghost' : 'soft'}
                    size="xs"
                    square
                    leading="solar:alt-arrow-left-linear"
                    disabled={!pageBackEnabled}
                    onClick={handleOnPageBackClick}
                />
                <input
                    className="c-genericTable__paginator_input"
                    type="number"
                    value={currentPage}
                    style={{width: `${getClosestValue(currentPage).toString().length + 3}ch`}}
                    onChange={handleOnChange}
                    onBlur={handleBlur}
                    onKeyDown={(e) => {
                        if (e.key === '-') e.preventDefault();
                    }}
                />
                <Button
                    variant={!pageNextEnabled ? 'ghost' : 'soft'}
                    size="xs"
                    square
                    leading="solar:alt-arrow-right-linear"
                    disabled={!pageNextEnabled}
                    onClick={handleOnPageNextClick}
                />
            </div>
        </div>
    );
}

export default Pagination;
